<template>
  <div>
    <base-full-loader/>
    <page-layout class="channels-header">
      <page-title>{{ $props.pageTitle }}</page-title>
      <channels-platform-select ref="platformSelect" @platform-selected="getTvChannels" @change="cancelSearch"/>
    </page-layout>
    <page-layout class="channels-body" v-show="!dataLoading">
      <div class="channels-sidebar">
        <page-sidebar :open="filtersOpen"
                      :sidebar-title="$t('filters.title')"
                      @close-sidebar="filtersOpen = false">
          <channels-sidebar-filters ref="filters"
                                    :filter-groups="filterGroups"
                                    @search-channels="doSearch($event, 'sidebar-search')"
                                    @filter-selected="applySelectedFilters"/>
        </page-sidebar>
      </div>
      <div class="channels-content">
        <channels-search-filter-toggle ref="mobileSearch"
                                       class="mobile-search"
                                       @search-channels="doSearch($event, 'mobile-search')"
                                       @filter-toggle="filtersOpen = !filtersOpen"/>
        <channels-list v-if="searchedChannelList.length"
                       :channels="searchedChannelList"
                       :package-links="$props.packageLinks"/>
        <channels-no-results v-else @cancel-search="cancelSearch"/>
      </div>
    </page-layout>
    <back-to-top/>
  </div>
</template>

<script>
import PageLayout from '../../../components/common/page-layout'
import PageTitle from '../../../components/common/page-title'
import debounce from '@/utils/debounce'
import { fetchTVChannels } from '../../../api/tvChannels'
import { createFiltersFromChannelData } from '../../../utils/channelsDataParser'
import ChannelsPlatformSelect from '../../../components/tv-channels/channels-platform-select'
import PageSidebar from '../../../components/common/page-sidebar'
import ChannelsSearchFilterToggle from '../../../components/tv-channels/channels-search-filter-toggle'
import ChannelsSidebarFilters from '../../../components/tv-channels/channels-sidebar-filters'
import ChannelsList from '../../../components/tv-channels/channels-list'
import ChannelsNoResults from '../../../components/tv-channels/channels-no-results'
import BackToTop from '../../../components/common/back-to-top'

export default {
  name: 'view-tv-page',
  components: {
    BackToTop,
    ChannelsNoResults,
    ChannelsList,
    ChannelsSidebarFilters,
    ChannelsSearchFilterToggle,
    PageSidebar,
    ChannelsPlatformSelect,
    PageTitle,
    PageLayout
  },
  data () {
    return {
      dataLoading: true,
      channelsData: [],
      filtersOpen: false,
      filterGroups: {},
      searchTerm: '',
      appliedFilters: {}
    }
  },
  props: {
    pageTitle: String,
    packagesAndAddonsAreTogether: {
      type: Boolean,
      default: true // ako se predomisle da addonovi ne idu u istoj pretrazi sa paketima, promeniti ovo na false
    },
    packageLinks: {
      type: String,
      default: ''
    }
  },
  computed: {
    searchedChannelList () {
      return this.filteredChannels.filter(channel => channel.name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1)
    },
    filtersRegexSource () {
      const applied = {
        packages: []
      }
      Object.entries(this.appliedFilters).forEach(entry => {
        const [key, val] = entry
        if (key === 'packages') {
          if (val) applied.packages.push(val)
          else applied.packages.push(null)
        } else if (this.$props.packagesAndAddonsAreTogether && key === 'addons') {
          // zahtev je da za addonove i pakete vazi pravilo "i"
          applied.packages = [...applied.packages, ...Object.keys(val)].filter(Boolean).join('|')
        } else if (Object.keys(val).length && key !== 'platform') {
          applied[key] = [...Object.keys(val)].filter(Boolean).join('|')
        }
      })
      return applied
    },
    // Najgluplji i najbrzi nacin koji sam mogao da smislim za pretragu, a da ne pisem neke tamo rekurzije i prolazim kroz sve propove
    filteredChannels () {
      const regExps = Object.values(this.filtersRegexSource).filter(Boolean).map(regexSrc => new RegExp(regexSrc, 'gi'))
      if (!regExps.length) return []
      return this.channelsData.filter(channel => {
        const stringified = JSON.stringify(channel)
        if (regExps.every(regEx => stringified.match(regEx))) {
          return channel
        }
      })
    }
  },
  methods: {
    async getTvChannels (platform) {
      this.$loader.show()
      const { data } = await fetchTVChannels(platform)
      if (data.products) {
        const filters = await createFiltersFromChannelData(data.products)
        this.filterGroups = { ...filters }
        this.channelsData = data.products
      } else {
        this.channelsData = []
      }
      this.$loader.hide()
      this.dataLoading = false
    },
    doSearch (e, type) {
      this.searchTerm = e

      // pošto nam je search u dve različite komponente, jedna za mob, a druga za desktop,
      // ovde ih izjednačavamo da se ne bi QA bunio kako mu se ne vidi isti tekst kad smanji ekran
      if (type === 'mobile-search') {
        this.$refs.filters.$refs.searchInput.searchText = this.searchTerm
      } else {
        this.$refs.mobileSearch.$refs.searchInput.searchText = this.searchTerm
      }
    },
    applySelectedFilters (e) {
      this.appliedFilters = { ...e }
    },
    cancelSearch () {
      this.$refs.filters.resetFilters()
      this.$refs.filters.clearSearch()
      this.$refs.mobileSearch.clearSearch()
    }
  },
  created () {
    window.addEventListener('resize', (e) => debounce(() => {
      this.$refs.platformSelect.emitPillsOffset()
      if (window.innerWidth >= 1024) {
        this.filtersOpen = false
      }
    }, 50))
  }
}
</script>

<style scoped lang="scss">
.channels-header {
  padding-bottom: 2.4rem;
  background: $white;

  @media (map_get($breakpoints, md)) {
    padding-bottom: 0;
  }
}

.channels-body {
  display: flex;
  padding-top: 4rem;

  .channels-sidebar {
    flex: 0 0 0;

    @media (map_get($breakpoints, md)) {
      flex-basis: 300px;
      min-width: 300px;
      z-index: 0;
    }
  }

  .channels-content {
    flex: 1 1 auto;

    @media (map_get($breakpoints, md)) {
      padding-left: 1.6rem;
    }
  }
}

.mobile-search {
  display: block;
  margin-bottom: 2.4rem;

  @media (map_get($breakpoints, md)) {
    display: none;
  }
}
</style>
